// src/cockpit4/components/TaskBoard.tsx
// ============================================
// P0-功能2：任务管理与追踪（项目中心）
//
// 功能：
//   1. 看板视图（按状态分列：待办/进行中/已完成/阻塞）
//   2. 列表视图切换
//   3. 任务卡片：标题、负责人、优先级、截止日期、关联项目
//   4. 任务创建/编辑/删除（弹窗表单）
//   5. 任务依赖关系设置（前置任务选择）
//   6. 拖拽调整状态（HTML5 drag and drop）
//   7. 任务评论（点击任务展开评论侧栏，localStorage 存储）
//   8. 关键路径识别（标记无依赖的最长任务链）
//
// 数据来源：
//   - localStorage 持久化（xeaos_tasks_list / xeaos_tasks_comments）
//   - 关联项目从 localStorage 读取自建项目（xeaos_projects_custom）
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckSquare,
  Plus,
  X,
  RefreshCw,
  User,
  Flag,
  Link2,
  MessageSquare,
  Trash2,
  Pencil,
  Clock,
  CheckCircle2,
  AlertOctagon,
  Loader2,
  Network,
  Calendar,
  Send,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 任务状态 */
export type TaskStatus = "todo" | "in_progress" | "done" | "blocked";

/** 任务优先级 */
export type TaskPriority = "low" | "medium" | "high" | "urgent";

/** 任务模型 */
export interface Task {
  id: string;
  title: string;
  description: string;
  assignee: string;
  priority: TaskPriority;
  status: TaskStatus;
  /** 截止日期 YYYY-MM-DD */
  dueDate: string;
  /** 关联项目 ID（本地自建项目） */
  projectId?: string;
  /** 前置任务 ID 列表（依赖关系） */
  dependencies: string[];
  /** 计划进度 0-100（基于时间线计算，用于偏差分析） */
  plannedProgress: number;
  /** 实际进度 0-100 */
  actualProgress: number;
  createdAt: string;
  updatedAt: string;
}

/** 任务评论 */
export interface TaskComment {
  id: string;
  taskId: string;
  author: string;
  content: string;
  createdAt: string;
}

// ============================================
// 常量配置
// ============================================

const TASKS_KEY = "xeaos_tasks_list";
const COMMENTS_KEY = "xeaos_tasks_comments";
const PROJECTS_KEY = "xeaos_projects_custom";

const STATUS_META: Record<
  TaskStatus,
  { label: string; color: string; icon: typeof Clock }
> = {
  todo: { label: "待办", color: "#6B7280", icon: Clock },
  in_progress: { label: "进行中", color: "#3B82F6", icon: Loader2 },
  done: { label: "已完成", color: "#10B981", icon: CheckCircle2 },
  blocked: { label: "阻塞", color: "#EF4444", icon: AlertOctagon },
};

/** 看板列顺序 */
const BOARD_COLUMNS: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];

const PRIORITY_META: Record<
  TaskPriority,
  { label: string; color: string }
> = {
  low: { label: "低", color: "#6B7280" },
  medium: { label: "中", color: "#3B82F6" },
  high: { label: "高", color: "#F59E0B" },
  urgent: { label: "紧急", color: "#EF4444" },
};

// ============================================
// localStorage 持久化
// ============================================

/** 加载任务列表 */
export function loadTasks(): Task[] {
  try {
    const raw = localStorage.getItem(TASKS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Task[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存任务列表 */
function saveTasks(tasks: Task[]): void {
  try {
    localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
  } catch {
    // 静默降级
  }
}

/** 加载评论列表 */
export function loadComments(): TaskComment[] {
  try {
    const raw = localStorage.getItem(COMMENTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as TaskComment[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存评论列表 */
function saveComments(comments: TaskComment[]): void {
  try {
    localStorage.setItem(COMMENTS_KEY, JSON.stringify(comments));
  } catch {
    // 静默降级
  }
}

/** 加载关联项目选项（从 localStorage 读取自建项目） */
function loadProjectOptions(): { id: string; name: string }[] {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Array<{ id: string; name: string }>;
    if (!Array.isArray(list)) return [];
    return list.map((p) => ({ id: p.id, name: p.name }));
  } catch {
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

/** 计算计划进度（基于截止日期，假设创建日为开始日） */
function calculatePlannedProgress(
  createdAt: string,
  dueDate: string
): number {
  const start = new Date(createdAt).getTime();
  const due = new Date(dueDate).getTime();
  if (isNaN(start) || isNaN(due) || due <= start) return 0;
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= due) return 100;
  return Math.round(((now - start) / (due - start)) * 100);
}

/** 格式化日期 */
function formatDate(iso: string): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/**
 * 关键路径识别：找到无依赖的最长任务链
 * 返回处于关键路径上的任务 ID 集合
 */
function findCriticalPathTaskIds(tasks: Task[]): Set<string> {
  if (tasks.length === 0) return new Set();

  // 构建依赖图：taskId → 依赖它的任务列表
  const dependents = new Map<string, Task[]>();
  tasks.forEach((t) => {
    t.dependencies.forEach((depId) => {
      if (!dependents.has(depId)) dependents.set(depId, []);
      dependents.get(depId)!.push(t);
    });
  });

  // 入口点：无前置依赖的任务
  const entryPoints = tasks.filter((t) => t.dependencies.length === 0);
  if (entryPoints.length === 0) return new Set();

  let longestPath: Task[] = [];

  // 深度优先搜索找最长链
  const dfs = (task: Task, path: Task[]) => {
    const newPath = [...path, task];
    const next = dependents.get(task.id) || [];
    if (next.length === 0) {
      if (newPath.length > longestPath.length) {
        longestPath = newPath;
      }
      return;
    }
    next.forEach((n) => dfs(n, newPath));
  };

  entryPoints.forEach((e) => dfs(e, []));

  // 只在有链条（长度 > 1）时标记
  if (longestPath.length <= 1) return new Set();
  return new Set(longestPath.map((t) => t.id));
}

// ============================================
// 主组件
// ============================================

export default function TaskBoard() {
  const [view, setView] = useState<"board" | "list">("board");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [projectOptions, setProjectOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [commentTaskId, setCommentTaskId] = useState<string | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TaskStatus | null>(null);

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    const loadedTasks = loadTasks();
    // 重新计算计划进度（时间推移后可能变化）
    const updated = loadedTasks.map((t) => ({
      ...t,
      plannedProgress: calculatePlannedProgress(t.createdAt, t.dueDate),
    }));
    setTasks(updated);
    setComments(loadComments());
    setProjectOptions(loadProjectOptions());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // 任务 CRUD
  // ============================================
  const handleSaveTask = useCallback(
    (taskData: Omit<Task, "id" | "createdAt" | "updatedAt" | "plannedProgress">) => {
      const now = new Date().toISOString();
      if (editingTask) {
        // 编辑现有任务
        const updated = tasks.map((t) =>
          t.id === editingTask.id
            ? {
                ...t,
                ...taskData,
                updatedAt: now,
                plannedProgress: calculatePlannedProgress(
                  t.createdAt,
                  taskData.dueDate
                ),
              }
            : t
        );
        setTasks(updated);
        saveTasks(updated);
      } else {
        // 新建任务
        const newTask: Task = {
          ...taskData,
          id: `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: now,
          updatedAt: now,
          plannedProgress: calculatePlannedProgress(now, taskData.dueDate),
        };
        const updated = [newTask, ...tasks];
        setTasks(updated);
        saveTasks(updated);
      }
      setShowEditModal(false);
      setEditingTask(null);
    },
    [editingTask, tasks]
  );

  const handleDeleteTask = useCallback(
    (taskId: string) => {
      const updated = tasks.filter((t) => t.id !== taskId);
      setTasks(updated);
      saveTasks(updated);
      // 同时清理该任务的评论和其它任务中对它的依赖
      const updatedComments = comments.filter((c) => c.taskId !== taskId);
      setComments(updatedComments);
      saveComments(updatedComments);
      const updatedDeps = updated.map((t) => ({
        ...t,
        dependencies: t.dependencies.filter((d) => d !== taskId),
      }));
      setTasks(updatedDeps);
      saveTasks(updatedDeps);
    },
    [tasks, comments]
  );

  // ============================================
  // 拖拽调整状态
  // ============================================
  const handleDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, taskId: string) => {
      setDraggingTaskId(taskId);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", taskId);
    },
    []
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, status: TaskStatus) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverStatus(status);
    },
    []
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, status: TaskStatus) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData("text/plain");
      if (taskId && draggingTaskId) {
        const updated = tasks.map((t) =>
          t.id === taskId
            ? { ...t, status, updatedAt: new Date().toISOString() }
            : t
        );
        setTasks(updated);
        saveTasks(updated);
      }
      setDraggingTaskId(null);
      setDragOverStatus(null);
    },
    [draggingTaskId, tasks]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingTaskId(null);
    setDragOverStatus(null);
  }, []);

  // ============================================
  // 评论管理
  // ============================================
  const handleAddComment = useCallback(
    (taskId: string, author: string, content: string) => {
      const newComment: TaskComment = {
        id: `comment_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        taskId,
        author: author || "匿名",
        content,
        createdAt: new Date().toISOString(),
      };
      const updated = [...comments, newComment];
      setComments(updated);
      saveComments(updated);
    },
    [comments]
  );

  // ============================================
  // 关键路径
  // ============================================
  const criticalPathIds = useMemo(
    () => findCriticalPathTaskIds(tasks),
    [tasks]
  );

  // 按状态分组（看板视图）
  const tasksByStatus = useMemo(() => {
    const map: Record<TaskStatus, Task[]> = {
      todo: [],
      in_progress: [],
      done: [],
      blocked: [],
    };
    tasks.forEach((t) => map[t.status].push(t));
    return map;
  }, [tasks]);

  // 项目名称映射
  const projectNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    projectOptions.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [projectOptions]);

  // 统计
  const stats = useMemo(() => {
    return {
      total: tasks.length,
      done: tasks.filter((t) => t.status === "done").length,
      blocked: tasks.filter((t) => t.status === "blocked").length,
      critical: criticalPathIds.size,
    };
  }, [tasks, criticalPathIds]);

  // 当前展开评论的任务
  const commentTask = commentTaskId
    ? tasks.find((t) => t.id === commentTaskId)
    : null;
  const taskComments = commentTaskId
    ? comments.filter((c) => c.taskId === commentTaskId)
    : [];

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-blue-500/15 border border-blue-500/30">
            <CheckSquare size={13} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              任务管理看板
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Task Board · {stats.total} 个任务 · 已完成 {stats.done} · 阻塞{" "}
              {stats.blocked} · 关键路径 {stats.critical}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 视图切换 */}
          <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setView("board")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "board"
                  ? "bg-blue-500/20 text-blue-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              看板
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                view === "list"
                  ? "bg-blue-500/20 text-blue-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              列表
            </button>
          </div>

          {/* 新增任务 */}
          <button
            onClick={() => {
              setEditingTask(null);
              setShowEditModal(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-500/15 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
          >
            <Plus size={11} className="text-blue-400" />
            <span className="text-[9px] font-mono text-blue-300 uppercase tracking-wider">
              新增任务
            </span>
          </button>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 关键路径提示 */}
      {criticalPathIds.size > 0 && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 border-b border-white/[0.04] bg-amber-500/[0.03]">
          <Network size={10} className="text-amber-400" />
          <span className="text-[9px] font-mono text-amber-400/80 uppercase tracking-wider">
            关键路径已标记 {criticalPathIds.size} 个任务（最长依赖链）
          </span>
        </div>
      )}

      {/* 主体内容 */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-blue-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载任务数据...
            </span>
          </div>
        ) : tasks.length === 0 ? (
          <EmptyState
            onAdd={() => {
              setEditingTask(null);
              setShowEditModal(true);
            }}
          />
        ) : view === "board" ? (
          /* 看板视图 */
          <div className="flex gap-3 overflow-x-auto pb-2">
            {BOARD_COLUMNS.map((status) => {
              const meta = STATUS_META[status];
              const SIcon = meta.icon;
              const columnTasks = tasksByStatus[status];
              const isDragOver = dragOverStatus === status;
              return (
                <div
                  key={status}
                  onDragOver={(e) => handleDragOver(e, status)}
                  onDragLeave={() => setDragOverStatus(null)}
                  onDrop={(e) => handleDrop(e, status)}
                  className={`flex-shrink-0 w-[260px] rounded-lg border transition-colors ${
                    isDragOver
                      ? "border-blue-500/40 bg-blue-500/[0.03]"
                      : "border-white/[0.04] bg-white/[0.01]"
                  }`}
                >
                  {/* 列头 */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
                    <div className="flex items-center gap-1.5">
                      <SIcon
                        size={11}
                        style={{ color: meta.color }}
                        className={status === "in_progress" ? "animate-spin" : ""}
                      />
                      <span
                        className="text-[10px] font-mono uppercase tracking-wider"
                        style={{ color: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <span className="text-[9px] font-mono text-gray-600">
                      {columnTasks.length}
                    </span>
                  </div>
                  {/* 列卡片 */}
                  <div className="p-2 space-y-2 max-h-[480px] overflow-y-auto">
                    {columnTasks.map((t) => (
                      <TaskCard
                        key={t.id}
                        task={t}
                        projectName={
                          t.projectId ? projectNameMap[t.projectId] : undefined
                        }
                        isCritical={criticalPathIds.has(t.id)}
                        isDragging={draggingTaskId === t.id}
                        onDragStart={(e) => handleDragStart(e, t.id)}
                        onDragEnd={handleDragEnd}
                        onEdit={() => {
                          setEditingTask(t);
                          setShowEditModal(true);
                        }}
                        onDelete={() => handleDeleteTask(t.id)}
                        onComment={() => setCommentTaskId(t.id)}
                      />
                    ))}
                    {columnTasks.length === 0 && (
                      <div className="text-center text-[9px] font-mono text-gray-700 py-4">
                        拖拽任务到此列
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* 列表视图 */
          <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
            {tasks.map((t) => (
              <TaskRow
                key={t.id}
                task={t}
                projectName={
                  t.projectId ? projectNameMap[t.projectId] : undefined
                }
                isCritical={criticalPathIds.has(t.id)}
                onEdit={() => {
                  setEditingTask(t);
                  setShowEditModal(true);
                }}
                onDelete={() => handleDeleteTask(t.id)}
                onComment={() => setCommentTaskId(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 任务编辑弹窗 */}
      <AnimatePresence>
        {showEditModal && (
          <TaskEditModal
            task={editingTask}
            allTasks={tasks}
            projectOptions={projectOptions}
            onSubmit={handleSaveTask}
            onClose={() => {
              setShowEditModal(false);
              setEditingTask(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* 评论侧栏 */}
      <AnimatePresence>
        {commentTask && (
          <CommentSidebar
            task={commentTask}
            comments={taskComments}
            onAddComment={handleAddComment}
            onClose={() => setCommentTaskId(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 空状态
// ============================================
function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <CheckSquare size={32} className="text-gray-700 mb-2" />
      <p className="text-[12px] text-gray-500 mb-1">暂无任务</p>
      <p className="text-[10px] font-mono text-gray-600 mb-3">
        点击「新增任务」创建第一个任务
      </p>
      <button
        onClick={onAdd}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
      >
        <Plus size={12} className="text-blue-400" />
        <span className="text-[10px] font-mono text-blue-300 uppercase tracking-wider">
          新增任务
        </span>
      </button>
    </div>
  );
}

// ============================================
// 任务卡片（看板视图）
// ============================================
function TaskCard({
  task,
  projectName,
  isCritical,
  isDragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onDelete,
  onComment,
}: {
  task: Task;
  projectName?: string;
  isCritical: boolean;
  isDragging: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onComment: () => void;
}) {
  const pMeta = PRIORITY_META[task.priority];
  const isOverdue =
    task.status !== "done" &&
    new Date(task.dueDate).getTime() < Date.now();

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`rounded-md border bg-white/[0.02] overflow-hidden cursor-grab active:cursor-grabbing transition-opacity ${
        isDragging ? "opacity-40" : "opacity-100"
      } ${
        isCritical
          ? "border-amber-500/30 ring-1 ring-amber-500/20"
          : "border-white/[0.04]"
      }`}
    >
      <div className="p-2.5">
        {/* 关键路径标记 */}
        {isCritical && (
          <div className="flex items-center gap-0.5 mb-1">
            <Network size={8} className="text-amber-400" />
            <span className="text-[8px] font-mono text-amber-400 uppercase tracking-wider">
              关键路径
            </span>
          </div>
        )}

        {/* 标题 */}
        <div className="text-[11px] text-gray-200 font-medium leading-snug mb-1.5">
          {task.title}
        </div>

        {/* 优先级 + 进度 */}
        <div className="flex items-center gap-1.5 mb-1.5">
          <span
            className="flex items-center gap-0.5 px-1 rounded text-[8px] font-mono uppercase"
            style={{ background: `${pMeta.color}20`, color: pMeta.color }}
          >
            <Flag size={7} />
            {pMeta.label}
          </span>
          <div className="flex-1 h-0.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-400"
              style={{ width: `${task.actualProgress}%` }}
            />
          </div>
          <span className="text-[8px] font-mono text-gray-500">
            {task.actualProgress}%
          </span>
        </div>

        {/* 元信息 */}
        <div className="flex items-center justify-between text-[9px] font-mono text-gray-600">
          <span className="flex items-center gap-0.5">
            <User size={8} />
            {task.assignee || "未分配"}
          </span>
          <span
            className={`flex items-center gap-0.5 ${
              isOverdue ? "text-red-400" : ""
            }`}
          >
            <Calendar size={8} />
            {formatDate(task.dueDate)}
            {isOverdue && " ⚠"}
          </span>
        </div>

        {/* 关联项目 + 依赖 */}
        {(projectName || task.dependencies.length > 0) && (
          <div className="flex items-center gap-1.5 mt-1 text-[8px] font-mono text-gray-600">
            {projectName && (
              <span className="truncate max-w-[100px]">{projectName}</span>
            )}
            {task.dependencies.length > 0 && (
              <span className="flex items-center gap-0.5">
                <Link2 size={7} />
                {task.dependencies.length} 依赖
              </span>
            )}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/[0.03]">
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
            title="编辑"
          >
            <Pencil size={9} />
          </button>
          <button
            onClick={onComment}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
            title="评论"
          >
            <MessageSquare size={9} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors ml-auto"
            title="删除"
          >
            <Trash2 size={9} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 任务行（列表视图）
// ============================================
function TaskRow({
  task,
  projectName,
  isCritical,
  onEdit,
  onDelete,
  onComment,
}: {
  task: Task;
  projectName?: string;
  isCritical: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onComment: () => void;
}) {
  const pMeta = PRIORITY_META[task.priority];
  const sMeta = STATUS_META[task.status];
  const isOverdue =
    task.status !== "done" &&
    new Date(task.dueDate).getTime() < Date.now();

  return (
    <div
      className={`rounded-md border bg-white/[0.02] p-2.5 ${
        isCritical
          ? "border-amber-500/30"
          : "border-white/[0.04]"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* 关键路径标记 */}
        {isCritical && (
          <Network size={10} className="text-amber-400 flex-shrink-0" />
        )}

        {/* 标题 */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-gray-200 font-medium truncate">
            {task.title}
          </div>
          {projectName && (
            <div className="text-[9px] font-mono text-gray-600 truncate">
              {projectName}
            </div>
          )}
        </div>

        {/* 进度 */}
        <div className="w-[60px] flex-shrink-0">
          <div className="text-[9px] font-mono text-gray-400 text-right">
            {task.actualProgress}%
          </div>
          <div className="h-0.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-400"
              style={{ width: `${task.actualProgress}%` }}
            />
          </div>
        </div>

        {/* 优先级 */}
        <span
          className="px-1 rounded text-[8px] font-mono uppercase flex-shrink-0"
          style={{ background: `${pMeta.color}20`, color: pMeta.color }}
        >
          {pMeta.label}
        </span>

        {/* 状态 */}
        <span
          className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase flex-shrink-0"
          style={{ background: `${sMeta.color}20`, color: sMeta.color }}
        >
          {sMeta.label}
        </span>

        {/* 负责人 */}
        <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500 flex-shrink-0 w-[70px]">
          <User size={8} />
          {task.assignee || "—"}
        </span>

        {/* 截止日 */}
        <span
          className={`flex items-center gap-0.5 text-[9px] font-mono flex-shrink-0 w-[80px] ${
            isOverdue ? "text-red-400" : "text-gray-500"
          }`}
        >
          <Calendar size={8} />
          {formatDate(task.dueDate)}
        </span>

        {/* 操作 */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
          >
            <Pencil size={9} />
          </button>
          <button
            onClick={onComment}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
          >
            <MessageSquare size={9} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
          >
            <Trash2 size={9} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 任务编辑弹窗
// ============================================
function TaskEditModal({
  task,
  allTasks,
  projectOptions,
  onSubmit,
  onClose,
}: {
  task: Task | null;
  allTasks: Task[];
  projectOptions: { id: string; name: string }[];
  onSubmit: (data: Omit<Task, "id" | "createdAt" | "updatedAt" | "plannedProgress">) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    assignee: task?.assignee || "",
    priority: task?.priority || ("medium" as TaskPriority),
    status: task?.status || ("todo" as TaskStatus),
    dueDate: task?.dueDate || today,
    projectId: task?.projectId || "",
    dependencies: task?.dependencies || ([] as string[]),
    actualProgress: task?.actualProgress ?? 0,
  });
  const [error, setError] = useState<string | null>(null);

  // 可选的依赖任务（排除自身和已依赖自身的任务，防止循环）
  const availableDeps = useMemo(() => {
    return allTasks.filter((t) => t.id !== task?.id);
  }, [allTasks, task]);

  const toggleDependency = (depId: string) => {
    setForm((prev) => ({
      ...prev,
      dependencies: prev.dependencies.includes(depId)
        ? prev.dependencies.filter((d) => d !== depId)
        : [...prev.dependencies, depId],
    }));
  };

  const handleSubmit = () => {
    if (!form.title.trim()) {
      setError("请输入任务标题");
      return;
    }
    if (!form.assignee.trim()) {
      setError("请输入负责人");
      return;
    }
    onSubmit({
      title: form.title,
      description: form.description,
      assignee: form.assignee,
      priority: form.priority,
      status: form.status,
      dueDate: form.dueDate,
      projectId: form.projectId || undefined,
      dependencies: form.dependencies,
      actualProgress: form.actualProgress,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-blue-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              {task ? <Pencil size={13} className="text-blue-400" /> : <Plus size={13} className="text-blue-400" />}
            </div>
            <h3 className="text-[13px] font-semibold text-white">
              {task ? "编辑任务" : "新增任务"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          <FormField label="任务标题">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="输入任务标题"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-blue-500/30"
            />
          </FormField>

          <FormField label="任务描述">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="简要描述任务内容"
              rows={2}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-blue-500/30 resize-none"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="负责人">
              <input
                type="text"
                value={form.assignee}
                onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                placeholder="负责人姓名"
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-blue-500/30"
              />
            </FormField>
            <FormField label="截止日期">
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-blue-500/30"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="优先级">
              <select
                value={form.priority}
                onChange={(e) =>
                  setForm({ ...form, priority: e.target.value as TaskPriority })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="low">低</option>
                <option value="medium">中</option>
                <option value="high">高</option>
                <option value="urgent">紧急</option>
              </select>
            </FormField>
            <FormField label="状态">
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as TaskStatus })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="todo">待办</option>
                <option value="in_progress">进行中</option>
                <option value="done">已完成</option>
                <option value="blocked">阻塞</option>
              </select>
            </FormField>
            <FormField label="进度">
              <input
                type="number"
                min={0}
                max={100}
                value={form.actualProgress}
                onChange={(e) =>
                  setForm({
                    ...form,
                    actualProgress: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                  })
                }
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-blue-500/30"
              />
            </FormField>
          </div>

          <FormField label="关联项目">
            <select
              value={form.projectId}
              onChange={(e) =>
                setForm({ ...form, projectId: e.target.value })
              }
              className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
            >
              <option value="">不关联项目</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FormField>

          {/* 前置任务（依赖关系） */}
          <FormField label="前置任务（依赖关系）">
            <div className="max-h-[120px] overflow-y-auto rounded-md border border-white/[0.06] bg-white/[0.02] p-2 space-y-1">
              {availableDeps.length === 0 ? (
                <div className="text-[9px] font-mono text-gray-600 text-center py-2">
                  暂无可选任务
                </div>
              ) : (
                availableDeps.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/[0.04] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.dependencies.includes(t.id)}
                      onChange={() => toggleDependency(t.id)}
                      className="w-3 h-3 accent-blue-500"
                    />
                    <span className="text-[10px] text-gray-400 truncate flex-1">
                      {t.title}
                    </span>
                    <span className="text-[8px] font-mono text-gray-600">
                      {PRIORITY_META[t.priority].label}
                    </span>
                  </label>
                ))
              )}
            </div>
          </FormField>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/30 text-[10px] font-mono text-blue-300 hover:bg-blue-500/30 transition-colors"
          >
            <CheckCircle2 size={10} />
            {task ? "保存" : "创建"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 评论侧栏
// ============================================
function CommentSidebar({
  task,
  comments,
  onAddComment,
  onClose,
}: {
  task: Task;
  comments: TaskComment[];
  onAddComment: (taskId: string, author: string, content: string) => void;
  onClose: () => void;
}) {
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = () => {
    if (!content.trim()) return;
    onAddComment(task.id, author, content);
    setContent("");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: 320 }}
        animate={{ x: 0 }}
        exit={{ x: 320 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm h-full flex flex-col bg-[#0F1623] border-l border-white/[0.08] shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <MessageSquare size={13} className="text-blue-400" />
            <div>
              <h3 className="text-[12px] font-semibold text-white">
                任务评论
              </h3>
              <p className="text-[9px] font-mono text-gray-500 truncate max-w-[180px]">
                {task.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 评论列表 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <MessageSquare size={24} className="text-gray-700 mb-1" />
              <p className="text-[10px] font-mono text-gray-600">
                暂无评论，添加第一条评论
              </p>
            </div>
          ) : (
            comments
              .slice()
              .reverse()
              .map((c) => (
                <div
                  key={c.id}
                  className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="flex items-center gap-1 text-[10px] font-mono text-gray-300">
                      <User size={8} />
                      {c.author}
                    </span>
                    <span className="text-[8px] font-mono text-gray-600">
                      {c.createdAt.slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 leading-snug whitespace-pre-wrap">
                    {c.content}
                  </p>
                </div>
              ))
          )}
        </div>

        {/* 输入区 */}
        <div className="p-3 border-t border-white/[0.06] space-y-2">
          <input
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="你的名字"
            className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-blue-500/30"
          />
          <div className="flex items-end gap-2">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="输入评论..."
              rows={2}
              className="flex-1 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-blue-500/30 resize-none"
            />
            <button
              onClick={handleSubmit}
              disabled={!content.trim()}
              className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-colors disabled:opacity-40"
            >
              <Send size={12} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
